import router from '@/router';
import { Component, Vue } from 'vue-property-decorator';
import axios from 'axios';

import {
  purchaseNumber,
  sendCheckoutEmailConfirmation,
  postCheckoutActions,
  invitedUserSubscription,
  registerKazoo,
  handleCheckoutDB,
  deleteTripodToken,
} from '@/api';

import { NumberInfo, CheckoutForm, CheckoutVerbose, Auth0User } from '@/shared/models/api.model';

import { formatNumber, formatLocation } from '@/shared/utils';

import ComputerHeaderStyle2 from '@/components/computer-header-style-2/computer-header-style-2.component';
import MobileHeader from '@/components/mobile-header/mobile-header.component';
import TabletHeader from '@/components/tablet-header/tablet-header.component';
import ContactSupport from '@/components/contact-support-component/contact-support.component';
import { supportEmail } from '@/main.ts';

Component.registerHooks(['beforeRouteEnter']);
@Component({
  name: 'invite-checkout',
  components: {
    ComputerHeaderStyle2,
    MobileHeader,
    TabletHeader,
    ContactSupport,
  },
})
export default class InviteCheckout extends Vue {
  // token to cancel api call...
  // currently not in use

  public cancelToken = axios.CancelToken;
  public cancelSource = this.cancelToken.source();
  public progressMessage: string = '';
  public numberInfo: NumberInfo = {
    number: '',
    npanxx: '',
    rate_center: '',
    region: '',
    location: '',
    trunk: '',
    country: '',
    token: '',
    api: '',
    number_to_order: '',
    new_location: '',
    status: '',
    reservation_id: null,
  };
  public auth0User: Auth0User = {
    given_name: '',
    family_name: '',
    email: '',
    user_id: '',
    sub: '',
    created_at: '',
    updated_at: '',
    email_verified: '',
    name: '',
    nickname: '',
    picture: '',
  };

  public country: string = JSON.parse(localStorage.getItem('location')!).country.name;

  public name: string;
  public email: string;
  public purchaseNumberError: boolean = false;
  public checkoutError: boolean = false;
  public checkoutInProgress: boolean = false;

  public subscriptionAdded: boolean = false;

  public mounted() {
    this.numberInfo = JSON.parse(sessionStorage.getItem('numberInfo')!);
    this.auth0User = JSON.parse(sessionStorage.getItem('auth0User')!);
    this.name = `${this.auth0User.given_name} ${this.auth0User.family_name}`;
    this.email = this.auth0User.email;
  }

  get getFormatNumber() {
    if (!formatNumber(this.numberInfo.number_to_order)) {
      return 'Pending...';
    }
    return formatNumber(this.numberInfo.number_to_order);
  }

  /** formats rate center */
  get getFormatRateCenter() {
    if (!formatLocation(this.numberInfo.location)) {
      return 'Pending...';
    }
    return formatLocation(this.numberInfo.location);
  }

  public handlePostAPICall(APIResponse: any, API: string) {
    if (APIResponse.error) {
      console.log(APIResponse.error);
      switch (API) {
        case 'StripeSubscription':
          this.progressMessage = `Creating a subscription failed... Please contact us at ${supportEmail}`;
          break;
        case 'Kazoo':
          this.progressMessage = `Kazoo account creation failed... please contact us at ${supportEmail}`;
          break;
        case 'Purchase':
          this.progressMessage = `Failed to purchase number... Please contact us at ${supportEmail}`;
          break;
        case 'DB':
          this.progressMessage = `Failed to make database connections... Please contact us at ${supportEmail}`;
          break;
      }

      this.checkoutError = true;
      this.checkoutInProgress = false;

      return false;
    }
    return true;
  }

  public async checkout() {
    // set timezone to maxmind or defautl to browser
    const timezone =
      JSON.parse(localStorage.getItem('location')!).timezone !== null
        ? JSON.parse(localStorage.getItem('location')!).timezone
        : Intl.DateTimeFormat().resolvedOptions().timeZone;

    this.checkoutInProgress = true;
    const inviteToken = localStorage.getItem('tokenValue')!;
    const polarisAccountId = localStorage.getItem('accountId')!;

    const checkoutVerbose: CheckoutVerbose = {
      stripeId: '',
      kazooUserId: '',
      kazooAccountId: '',
      numberPurchased: '',
      auth0userId: this.auth0User.user_id ? this.auth0User.user_id : this.auth0User.sub,
      invited: true,
      portNumber: sessionStorage.getItem('portNumber') === 'true' || false,
    };
    let subscriptionResponse = null;

    // STEP 1: INCREASE ADMIN'S SUBSCRIPTION QUANTITY BY 1 IF HAVENT ALREADY
    if (!this.subscriptionAdded) {
      this.progressMessage = 'Adding subscription';
      subscriptionResponse = await invitedUserSubscription<any>(inviteToken, this.cancelSource.token);
      if (!this.handlePostAPICall(subscriptionResponse, 'StripeSubscription')) {
        return;
      } else {
        checkoutVerbose.stripeId = subscriptionResponse.data.id;
      }
    }

    // STEP 2: USE KAZOO API TO CREATE KAZOO ACCOUNT AND USER
    this.progressMessage = 'Creating your calling account...';
    const checkoutForm: CheckoutForm = {
      address1: 'placeholder',
      address2: null,
      email: this.email,
      company: 'placeholder',
      name: this.name,
      country: JSON.parse(localStorage.getItem('location')!).country.iso_code || null,
      city: 'placeholder',
      stateProvince: 'placeholder',
    };

    const kazooResponse = await registerKazoo<any>(checkoutForm, timezone, polarisAccountId);

    if (!this.handlePostAPICall(kazooResponse, 'Kazoo')) {
      return;
    } else {
      checkoutVerbose.kazooUserId = kazooResponse.data.kazoo_user_id;
      checkoutVerbose.kazooAccountId = kazooResponse.data.kazoo_account_id;
    }

    // // STEP 3: PURCHASE NUMBER
    let purchaseNumberResponse: any = '';
    let purchaseSuccess = false;

    // while (retries < maxRetries || !purchaseSuccess) {
    this.progressMessage = 'Purchasing your new Corvum number...';
    if (this.numberInfo && this.numberInfo.api) {
      // Purchase it
      console.log(this.numberInfo);

      purchaseNumberResponse = await purchaseNumber(this.numberInfo, this.email, this.cancelSource.token);
      // If Error, Find a new number and purchase it...
      // if (purchaseNumberResponse.error) {
      //   sessionStorage.setItem('numberInfo', JSON.stringify(purchaseNumberResponse.data));
      //   this.numberInfo = JSON.parse(sessionStorage.getItem('numberInfo')!);
      //   this.progressMessage = `Failed purchasing original number... \
      //     Found new number ${this.numberInfo.number_to_order}`;
      //   // purchase was successful
      // } else {
      // purchaseSuccess = true;
      // }
    }
    if (!this.handlePostAPICall(purchaseNumberResponse, 'Purchase')) {
      return;
    } else {
      checkoutVerbose.numberPurchased = this.numberInfo.number_to_order;
    }

    // STEP 4: DB QUERY: CREATE ALL CRDB FIELDS IN ONE CALL
    // return fail if >=1 creation has failed
    this.progressMessage = 'Creating database connections...';

    /**
     * Handles DB commits for:
     *    kazoo_changes,
     *    number_changes,
     *    auth0_changes,
     *    domain_changes,
     */
    const DBResponse = await handleCheckoutDB<any>(
      null,
      null,
      kazooResponse.data,
      this.numberInfo,
      this.auth0User,
      checkoutVerbose.invited,
      timezone,

      // JSON.parse(localStorage.getItem('location')!).timezone,
      this.cancelSource.token,
    );
    if (!this.handlePostAPICall(DBResponse, 'DB')) {
      return;
    }

    // STEP 5: SEND POST CHECKOUT ACTIONS

    // list of async functions to wait on
    const promisesToResolve = [];
    if (!this.checkoutError) {
      promisesToResolve.push(
        // delete token from DB
        deleteTripodToken(inviteToken),
      );
      promisesToResolve.push(
        // internal email for successful client onboard info
        sendCheckoutEmailConfirmation<any>(this.numberInfo.number_to_order, this.email, this.name, checkoutVerbose),
      );
      // registers user to AC
      promisesToResolve.push(
        postCheckoutActions<any>(
          this.numberInfo.number_to_order,
          checkoutForm,
          DBResponse.data.created_domain || null,
          this.email,
          checkoutVerbose.invited,
          polarisAccountId,
        ),
      );
      // ensures functions are done before pushing to next page
      Promise.all(promisesToResolve).then(() => {
        console.log('I sent some emails.');
      });
    }
    this.checkoutInProgress = false;
    router.push('checkoutconfirmation');
  }
}
